import { useState, useEffect } from 'react';
import { useFile, useVisualizer, useUI } from '../../context/AppContext.jsx';

export default function VisualizationControls() {
  const { currentWaypoints } = useFile();
  const { visualizer } = useVisualizer();
  const { jumpToWaypointEnabled, setJumpToWaypointEnabled, currentRobotType, setCurrentRobotType } = useUI();
  const [showLabels, setShowLabels] = useState(true);
  const [showRobot, setShowRobot] = useState(true);
  const [showFrames, setShowFrames] = useState(false);
  const [showJointControls, setShowJointControls] = useState(false);
  const [sphereSize, setSphereSize] = useState(1.0);
  const [jointAngles, setJointAngles] = useState([0, -60, -120, -90, 90, 0]);

  useEffect(() => {
    if (visualizer && visualizer.robotLoaded && visualizer.ur10) {
      try {
        const angles = visualizer.ur10.getJointAngles();
        setJointAngles(angles);
      } catch (err) {
        console.warn('Failed to read robot joint angles:', err);
      }
    }
  }, [visualizer, showJointControls]);

  const toggleLabels = () => {
    const newShowLabels = !showLabels;
    setShowLabels(newShowLabels);
    if (visualizer) {
      visualizer.toggleLabels(newShowLabels);
    }
  };

  const toggleRobot = () => {
    if (visualizer) {
      visualizer.setRobotVisible(!showRobot);
      setShowRobot(!showRobot);
    }
  };

  const toggleFrames = () => {
    const newShowFrames = !showFrames;
    setShowFrames(newShowFrames);
    if (visualizer) {
      visualizer.setFeaturesVisible(newShowFrames);
    }
  };

  const toggleJointControls = () => {
    setShowJointControls(!showJointControls);
    if (visualizer && visualizer.robotLoaded && visualizer.ur10) {
      try {
        const angles = visualizer.ur10.getJointAngles();
        setJointAngles(angles);
      } catch (err) {
        console.warn('Failed to read robot joint angles:', err);
      }
    }
  };

  const handleRobotTypeChange = async (e) => {
    const newType = e.target.value;
    setCurrentRobotType(newType);
    if (visualizer) {
      await visualizer.addRobotModel(newType);
      visualizer.setRobotVisible(showRobot);
    }
  };

  const handleSphereSizeChange = (e) => {
    const size = parseFloat(e.target.value);
    setSphereSize(size);
    if (visualizer) {
      visualizer.setSphereSize(size);
    }
  };

  const handleJointChange = (jointIndex, value) => {
    const newAngles = [...jointAngles];
    newAngles[jointIndex] = parseFloat(value);
    setJointAngles(newAngles);
    
    if (visualizer && visualizer.ur10) {
      visualizer.ur10.setJointAngles(newAngles);
    }
  };

  const resetJoints = () => {
    const homeAngles = [0, -60, -120, -90, 90, 0];
    setJointAngles(homeAngles);
    if (visualizer && visualizer.ur10) {
      visualizer.ur10.setJointAngles(homeAngles);
    }
  };

  const jointNames = ['Base (J1)', 'Shoulder (J2)', 'Elbow (J3)', 'Wrist 1 (J4)', 'Wrist 2 (J5)', 'Wrist 3 (J6)'];
  const jointRanges = [
    { min: -360, max: 360 },
    { min: -360, max: 360 },
    { min: -180, max: 180 },
    { min: -360, max: 360 },
    { min: -360, max: 360 },
    { min: -360, max: 360 },
  ];

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={toggleLabels}
        className={`bg-bg-tertiary text-text-primary px-3 py-1.5 rounded hover:bg-bg-hover transition-colors ${showLabels ? 'opacity-100' : 'opacity-50'}`}
        title="Toggle Waypoint Labels"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      </button>
      <button
        onClick={toggleRobot}
        className={`bg-bg-tertiary text-text-primary px-3 py-1.5 rounded hover:bg-bg-hover transition-colors ${showRobot ? 'opacity-100' : 'opacity-50'}`}
        title="Toggle Robot Model"
      >
        <svg className="w-5 h-5" viewBox="0 0 17.647 17.647" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <g>
            <g>
              <circle cx="2.648" cy="7.546" r="1.11"></circle>
              <path d="M5.296,7.546c0-1.46-1.188-2.648-2.648-2.648C1.189,4.898,0,6.086,0,7.546s1.188,2.648,2.648,2.648 C4.108,10.194,5.296,9.006,5.296,7.546z M2.648,9.567c-1.114,0-2.021-0.906-2.021-2.021s0.907-2.021,2.021-2.021 c1.115,0,2.021,0.906,2.021,2.021S3.762,9.567,2.648,9.567z"></path>
              <path d="M5.655,9.221C5.527,9.576,5.324,9.91,5.041,10.194c-0.306,0.305-0.668,0.516-1.052,0.641 l2.896,2.898h-0.56v2.035h5.435v-2.035h-1.596L5.655,9.221z"></path>
              <path d="M17.572,4.686c-0.104-0.104-0.271-0.107-0.376-0.004l-2.418,2.364l-1.78-0.934l-0.467-2.034 l1.192-1.618l3.206,1.073c0.049,0.016,0.099,0.018,0.146,0.007c0.087-0.02,0.162-0.083,0.191-0.175 c0.049-0.139-0.027-0.291-0.166-0.337l-3.391-1.135c-0.11-0.038-0.23,0.001-0.298,0.094l-1.085,1.47l-0.895,0.205l0.126,0.548 L5.294,5.648c0.232,0.297,0.407,0.646,0.497,1.038c0.096,0.42,0.085,0.839-0.014,1.232l6.298-1.447l0.126,0.548L13.154,6.8 l-0.002-0.004L14.7,7.609c0.059,0.03,0.124,0.038,0.184,0.023c0.047-0.011,0.092-0.034,0.126-0.069l2.556-2.501 C17.672,4.96,17.674,4.791,17.572,4.686z"></path>
            </g>
          </g>
        </svg>
      </button>
      <button
        onClick={toggleJointControls}
        className={`bg-bg-tertiary text-text-primary px-3 py-1.5 rounded hover:bg-bg-hover transition-colors ${showJointControls ? 'opacity-100' : 'opacity-50'}`}
        title="Toggle Joint Controls"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"></circle>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"></path>
        </svg>
      </button>
      <button
        onClick={toggleFrames}
        className={`bg-bg-tertiary text-text-primary px-3 py-1.5 rounded hover:bg-bg-hover transition-colors ${showFrames ? 'opacity-100' : 'opacity-50'}`}
        title="Toggle Feature Frames"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          {/* X axis (right arrow) */}
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 12h9m-2-2l2 2-2 2" />
          {/* Y axis (down arrow) */}
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v9m-2-2l2 2 2-2" />
          {/* Z axis (up-left arrow) */}
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 12L3 3m2 0l-2 2 2 2" />
        </svg>
      </button>
      <select
        value={currentRobotType}
        onChange={handleRobotTypeChange}
        className="bg-bg-tertiary text-text-primary px-3 py-1.5 rounded border border-border"
      >
        <option value="ur5e">UR5e</option>
        <option value="ur8long">UR8 Long</option>
        <option value="ur10e">UR10e</option>
        <option value="ur20">UR20</option>
      </select>
      <button
        onClick={() => setJumpToWaypointEnabled(!jumpToWaypointEnabled)}
        className={`bg-bg-tertiary text-text-primary px-3 py-1.5 rounded hover:bg-bg-hover transition-colors ${jumpToWaypointEnabled ? 'opacity-100 bg-primary bg-opacity-20' : 'opacity-50'}`}
        title="Toggle Jump to Waypoint"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <polyline strokeLinecap="round" strokeLinejoin="round" points="9 10 4 15 9 20"></polyline>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 4v7a4 4 0 0 1-4 4H4"></path>
        </svg>
      </button>
      <div className="flex items-center gap-2">
        <label className="text-xs text-text-secondary whitespace-nowrap">Size:</label>
        <input
          type="range"
          min="0.2"
          max="2.0"
          step="0.1"
          value={sphereSize}
          onChange={(e) => handleSphereSizeChange(e)}
          className="w-24"
          title="Adjust sphere size"
        />
      </div>

      {/* Joint Controls Panel */}
      {showJointControls && (
        <div className="absolute top-full right-0 mt-2 bg-bg-secondary border border-border rounded-lg p-4 z-10 min-w-[300px] shadow-lg">
          <div className="space-y-3">
            {jointAngles.map((angle, index) => (
              <div key={index} className="flex items-center gap-2">
                <label className="text-sm text-text-secondary w-24">{jointNames[index]}:</label>
                <input
                  type="range"
                  min={jointRanges[index].min}
                  max={jointRanges[index].max}
                  step="1"
                  value={angle}
                  onChange={(e) => handleJointChange(index, e.target.value)}
                  className="flex-1"
                />
                <span className="text-sm text-text-primary w-12 text-right">{angle.toFixed(0)}°</span>
              </div>
            ))}
            <button
              onClick={resetJoints}
              className="w-full bg-bg-tertiary text-text-primary px-4 py-2 rounded hover:bg-bg-hover transition-colors mt-2"
            >
              Move To Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
